import {Component, inject, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {Router, RouterOutlet, RoutesRecognized} from '@angular/router';
import {IntercomService, updateIntercom} from "./services/ui/intercom.service";
import {PosthogService} from "./services/analytics/posthog.service";
import {filter} from "rxjs/operators";
import {ConnectionBannerComponent} from "./modules/global/connection-banner/connection-banner.component";
import {SnackbarComponent} from './modules/global/snackbar/snackbar.component';

// Default CD (NOT OnPush): an OnPush root gates change detection for the entire legacy
// default-CD subtree below it — the app renders once then freezes (blank screen).
@Component({
	selector: 'app-root',
	imports: [RouterOutlet, ConnectionBannerComponent, SnackbarComponent],
	templateUrl: './app.component.html',
	// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit {
	private translateService = inject(TranslateService);
	private router = inject(Router);
	private intercomService = inject(IntercomService);
	// Injected for its side effect: PostHog init. (The Cmd+K search shortcut is
	// instantiated by HomeComponent so it only exists in the logged-in area.)
	private posthogService = inject(PosthogService);

	ngOnInit(): void {
		this.removeSplashScreen();

		const lang = localStorage.getItem('lang') || this.translateService.getBrowserLang();

		if (lang === 'en' || lang === 'fr') {
			localStorage.setItem('lang', lang);
			this.translateService.setFallbackLang(lang);
			this.translateService.use(lang);
		} else {
			localStorage.setItem('lang', 'en');
			this.translateService.setFallbackLang('en');
			this.translateService.use('en');
		}

		// Subscribe to router events for Intercom updates.
		this.router.events.pipe(
			filter(event => event instanceof RoutesRecognized),
			updateIntercom(this.intercomService)
		).subscribe();
	}

	// The splash overlay lives in index.html, outside <app-root>. By the time this component
	// initializes, the initial navigation is done (enabledBlocking) — remove the splash once
	// its minimum display time (set in index.html) has elapsed.
	private removeSplashScreen(): void {
		const splash = document.getElementById('splash-screen');
		if (!splash) {
			return;
		}
		const minHideTime = (window as unknown as { splashMinHideTime?: number }).splashMinHideTime ?? 0;
		setTimeout(() => splash.remove(), Math.max(0, minHideTime - Date.now()));
	}
}
